import { Box, Grid } from '@mui/material'
import React, { useCallback, useEffect, useMemo } from 'react'
import CalculatePanel from '../CalculationInput/CalculatePanel'
import { PaymentDisplaySection } from './hook/SectionType'
import { PaymentDetail } from './PaymentDetail'
import CreditCardPanel from './CreditCardPanel'
import AddTipsSection from './AddTipsSection/AddTipsSection'
import usePaymentContext from './hook/usePaymentContext'
import PaymentRedeemPoint from './RedeemPointSection/PaymentRedeemPoint'
import { useAppSelector } from 'store/useStoreHook'
import useFetchApi from 'hooks/useFetchApi/useFetchApi'
import customerService from 'Services/api/customer.service'
import GiftCardPaymentSection from './GiftCardPayment/GiftCardPaymentSection'
import CustomerAccountPaymentForm from './dialogs/CustomerAccountsSellPopup/CustomerAccountPaymentForm'
import { usePosContext } from 'views/Sale/contexts/PosProvider'

const PaymentMainSection = ({
    onSubmitPayment,
    isSplitPayment,
}: {
    onSubmitPayment: () => Promise<string | void>
    isSplitPayment?: boolean
}) => {
    const { isOffline } = usePosContext()
    const isLoyaltyModuleEnabled = useAppSelector(
        (state) => state.operation.data.loyalty,
    )
    const [customerDetail, fetchCustomerSlimByID] = useFetchApi(
        customerService.getCustomerSlimById,
    )

    const {
        quickButtons,
        onSetTenderedAmount,
        paymentDetail,
        tenderedAmount,
        onOpenAccountDialog,
        currentSection: displaySection,
        addTipsMode,
        tipsRate,
        onSetTipsAmount,
        tipsType,
        noQuickTip,
        onSetShowRedeemPointForm,
        showRedeemPointForm,
        pointRedemptionSetting,
        onRedeemSubmit,
        orderCustomerID: selectedCustomerId,
        orderDetail,
        customerList,
        onSetReceivedCash,
        isNegativeTotalDue,
        hasAwaitingProcessTransaction,
    } = usePaymentContext()

    const onChange = useCallback(
        (amount: number) => {
            onSetReceivedCash(amount)
            // check in case deposit-and-credit item
            if (amount < 0) onSetTenderedAmount(0)
            else onSetTenderedAmount(amount)
        },
        [onSetReceivedCash, onSetTenderedAmount],
    )

    useEffect(() => {
        if (!isOffline && selectedCustomerId) {
            fetchCustomerSlimByID(selectedCustomerId)
        }
    }, [fetchCustomerSlimByID, isOffline, selectedCustomerId])

    const onShowRedeemPoint = useCallback(
        (click: boolean) => onSetShowRedeemPointForm(click),
        [onSetShowRedeemPointForm],
    )
    const customer = useMemo(
        () =>
            customerDetail ||
            customerList.find((i) => i.customerID === selectedCustomerId),
        [customerList, customerDetail, selectedCustomerId],
    )
    const useRedeempoint =
        isLoyaltyModuleEnabled && !isSplitPayment && !customer?.isDefault
    const disabledRedeemPointRule = useMemo(
        () =>
            customer?.isDefault ||
            !customer ||
            !pointRedemptionSetting?.monetaryValue ||
            !pointRedemptionSetting?.requiredPoint ||
            // Disable to redeem point if Total Due is Negative
            isNegativeTotalDue ||
            hasAwaitingProcessTransaction,
        [
            customer,
            hasAwaitingProcessTransaction,
            pointRedemptionSetting?.monetaryValue,
            pointRedemptionSetting?.requiredPoint,
            isNegativeTotalDue,
        ],
    )

    if (addTipsMode)
        return (
            <AddTipsSection
                onSubmitPayment={onSubmitPayment}
                quickButtons={quickButtons}
                tipsRate={tipsRate}
                onSetTipsAmount={onSetTipsAmount}
                tipsType={tipsType}
                noQuickTip={noQuickTip}
                disableSwitchMode={isNegativeTotalDue}
            />
        )

    if (displaySection === PaymentDisplaySection.AccountList)
        return <CustomerAccountPaymentForm />
    else if (
        displaySection === PaymentDisplaySection.Calculation ||
        displaySection === PaymentDisplaySection.CreditCard
    ) {
        if (showRedeemPointForm && customer)
            return (
                <PaymentRedeemPoint
                    pointRedemptionSetting={pointRedemptionSetting}
                    onSetShowRedeemPointForm={onSetShowRedeemPointForm}
                    onRedeemSubmit={onRedeemSubmit}
                    customer={customer}
                    orderDetail={orderDetail}
                />
            )
        else
            return (
                <Grid container spacing={1}>
                    <Grid
                        item
                        xs={12}
                        md={7}
                        lg={8}
                        height={(x) => `calc(75svh - ${x.spacing(1)})`}
                    >
                        {displaySection === PaymentDisplaySection.CreditCard ? (
                            <CreditCardPanel
                                tenderedAmount={tenderedAmount.toNumber()}
                                useRedeemPointButton={useRedeempoint}
                                isRedeemPointMode={showRedeemPointForm}
                                onClickRedeemPoint={onShowRedeemPoint}
                                disabledRedeemPoint={disabledRedeemPointRule}
                            />
                        ) : (
                            <CalculatePanel
                                paymentDetail={paymentDetail}
                                quickButtons={quickButtons}
                                initialValue={tenderedAmount.toNumber()}
                                onChange={onChange}
                                onChangeCustomerAccount={onOpenAccountDialog}
                                useRedeemPointButton={useRedeempoint}
                                isRedeemPointMode={showRedeemPointForm}
                                onClickRedeemPoint={onShowRedeemPoint}
                                disabledRedeemPoint={disabledRedeemPointRule}
                            />
                        )}
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={5}
                        lg={4}
                        height={(x) => `calc(75svh - ${x.spacing(1)})`}
                    >
                        <PaymentDetail onSubmit={onSubmitPayment} />
                    </Grid>
                </Grid>
            )
    } else if (displaySection === PaymentDisplaySection.GiftCard)
        return <GiftCardPaymentSection />

    return <Box></Box>
}

export default PaymentMainSection
